import { Lightbox, LightboxItem } from '@design-system';
import classNames from 'classnames';
import { useMemo } from 'react';
import { render } from 'storyblok-rich-text-react-renderer-ts';

import { Columns } from '../helpers/images';
import { MultiImageStoryblok } from '../types/generated-storyblok';
import { TrackEvent } from '../utils/analytics';
import { isStoryblokRichTextPresent } from '../utils/renderStoryblokRichText';
import { Image } from './Image';
import { InlineVideo } from './InlineVideo/InlineVideo';

const calculateSpan = (
  containerSize: MultiImageStoryblok['containerSize'],
  imageLength: number,
) => {
  switch (containerSize) {
    case 'wide':
      return 6 / imageLength;
    case 'ultrawide':
      return 8 / imageLength;
    case 'max':
      return 12 / imageLength;
    default:
      return 12;
  }
};

interface Blok extends MultiImageStoryblok {
  analytics?: Analytics;
}

interface ContentBlockImageProps {
  blok: Blok;
}

export const ContentBlockImage = ({ blok }: ContentBlockImageProps) => {
  const span = calculateSpan(
    blok.containerSize,
    (blok.images || []).length,
  ) as Columns;

  const lightboxItems: LightboxItem[] = useMemo(
    () =>
      blok.images.map((item, key) => {
        if (item.component === 'inlineVideo') {
          return {
            key,
            media: {
              id: item.videoId,
              type: 'VIMEO',
            },
            copyright: item.cover.copyright,
          };
        }

        return {
          key,
          media: {
            alt: item.image.alt,
            src: item.image.filename,
            type: 'IMAGE',
          },
          copyright: item.image.copyright,
        };
      }),
    [blok],
  );

  return (
    <Lightbox items={lightboxItems}>
      {(lightbox) => (
        <div
          key={blok._uid}
          className={classNames(
            'mx-auto flex w-full flex-col items-start justify-center gap-6 md:flex-row',
            {
              'max-w-xl': blok.containerSize === 'wide',
              'max-w-5xl': blok.containerSize === 'ultrawide',
            },
          )}
        >
          {blok.images.map((media, index) => {
            if (media.component === 'inlineVideo') {
              return (
                <div className="group w-full flex-1">
                  <InlineVideo
                    autoplay={media.autoplay}
                    className="flex-1"
                    cover={media.cover.filename}
                    onPlay={() => {
                      if (blok.analytics) {
                        TrackEvent(`${blok.analytics.page} video clicked`, {
                          ...blok.analytics,
                          title: media.alt || media.cover?.alt || '',
                        });
                      }
                    }}
                    platform={media.platform}
                    playButton={media?.playButton ?? false}
                    ratio={media.ratio}
                    title={media.cover?.alt ?? ''}
                    videoId={media.videoId}
                  />
                </div>
              );
            }

            return (
              <figure key={media.image.id} className="group w-full flex-1">
                <Image
                  alt={media.image?.alt ?? ''}
                  className="overflow-hidden rounded-sm"
                  hoverEffect
                  onClick={() => {
                    lightbox.openOnSlide(index);
                    if (blok.analytics) {
                      TrackEvent(`${blok.analytics.page} image clicked`, {
                        ...blok.analytics,
                        title: media.image?.alt || '',
                      });
                    }
                  }}
                  size={{
                    sm: { span: 12, ratio: 'intrinsic' },
                    md: { span, ratio: 'intrinsic' },
                    lg: { span, ratio: 'intrinsic' },
                  }}
                  src={media.image.filename}
                />
                {isStoryblokRichTextPresent(media.caption) && (
                  <figcaption className="mt-3">
                    <p className="aa-prose text-xs text-neutral-4">
                      {render(media.caption)}
                    </p>
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      )}
    </Lightbox>
  );
};
